import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Logger, type AppState } from '@nextgen-web-framework/core';
import {
  DynamicObjectInterface,
  RegistrationFlowDataType,
  PersonalIdRecordTypeEnum,
  RoutePaths,
} from '@commerce-profile-webcomponents/sdk';
import { PageLoader } from '@commerce-webcomponents/ui';
import { useNextgenRouter } from '@nextgen-web-framework/all';
import RegistrationPersonalDetailsStep from '../../RegistrationPersonalDetailsStep/RegistrationPersonalDetailsStep';
import { NextHandlerProps, APICallType } from '../../Registration/types/propTypes';
import logObject from '../../../../logObject';
import { CoAppCommonProps, CoAppScreenEnum } from '../types/propTypes';
import CoAppCommonScreen from '../CoAppCommonScreen/CoAppCommonScreen';
import styles from './CoApplicantUserDetails.module.scss';

const CoApplicantUserDetails = ({ nextHandler, ...props }: CoAppCommonProps) => {
  const { push } = useNextgenRouter();
  const [showConfirmationScreen, setShowConfirmationScreen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const logger = Logger.getInstance(logObject('CoApplicantUserDetails', 'CoApplicantUserDetails.tsx'));

  const registrationData = useSelector(
    (state: AppState) => state.commerceProfile.registrationFlowData,
  ) as RegistrationFlowDataType;
  const { cidVerification, initialRegistrationForm } = registrationData;
  const { inputForm } = initialRegistrationForm;

  const handleNextButton = async (
    payload?: NextHandlerProps,
    slicePayload?: DynamicObjectInterface,
    _stayOnSameStep?: boolean,
    callOnSuccess?: boolean,
  ) => {
    // prepare coapp data
    const coAppData = {
      verifyPersonalId: cidVerification?.spousePersonalId,
      personalId: {
        recordNumber: cidVerification?.spousePersonalId,
        recordType: cidVerification?.spouseRecordType as PersonalIdRecordTypeEnum,
        documentData: payload?.personalId?.documentData,
      },
    };

    if (payload?.identityJWT) {
      setIsLoading(true);
      let res: DynamicObjectInterface = {};
      try {
        const patchPayload: NextHandlerProps = {
          ...payload,
          version: payload?.version ?? 0,
          phone: inputForm?.userName,
          ...coAppData,
        };
        const patchRes = await Promise.resolve(
          nextHandler?.(patchPayload, slicePayload, true, callOnSuccess, APICallType.PATCH),
        );
        // Final call
        const finalPayload: NextHandlerProps = {
          ...payload,
          fullUrl: '/signup', // dummy data
          version: patchRes?.version ?? 0,
          phone: inputForm?.userName,
          ...coAppData,
        };
        res = await Promise.resolve(nextHandler?.(finalPayload, slicePayload, true, callOnSuccess, APICallType.SUBMIT));

        setShowConfirmationScreen(true);
        setIsLoading(false);
      } catch (error) {
        logger.error('Error occurred while handling next button:', error);
      } finally {
        setIsLoading(false);
      }
      return res;
    } else {
      // Upload document
      const finalPayload: NextHandlerProps = {
        ...payload,
        ...coAppData,
        version: payload?.version ?? 0,
      };
      return await Promise.resolve(nextHandler?.(finalPayload, slicePayload, true, callOnSuccess, APICallType.PATCH));
    }
  };

  const handleBackButton = () => {
    push?.(RoutePaths.HOME);
  };

  if (showConfirmationScreen) {
    return <CoAppCommonScreen screenType={CoAppScreenEnum.COMPLETED} buttonHandler={handleBackButton} />;
  } else {
    // To reuse or copy the code from RegistrationPersonalDetailsStep
    return (
      <>
        <div className={styles['co-app-user-details-step-container']} id="co-app-user-details-step-container">
          <RegistrationPersonalDetailsStep {...props} nextHandler={handleNextButton} hideAddressFields isSpouse />
        </div>
        {isLoading && <PageLoader />}
      </>
    );
  }
};
export default CoApplicantUserDetails;
